import {useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {PageContainer} from '@/components/organisms';
import {PerfectStoreDisplay} from '@/containers';
import PerfectStoreWizardLayout from '@/containers/PerfectStore/PerfectStoreWizardLayout';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetPerfectStore,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {ROUTES} from '@/utils/constants/routesConstants';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreDisplayInvalid
} from '@/pages/PerfectStoreUtils';
import {getPerfectStoreWizardReviewRoute} from '@/helpers/perfectStoreUtils';
import {
  CREATE_PERFECT_STORE_TITLE,
  isDisplayStepApplicable
} from '@/utils/constants/perfectStoreConstants';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {logger} from '@/helpers/logger';

export const AddPerfectStoreDisplay = () => {
  const location = useLocation();
  const isEdit = location?.state?.isEdit ?? false;
  const disableDraft = location?.state?.disableDraft ?? false;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const perfectStore = useAppSelector((state) => state?.perfectStore);
  const formDataFromStore = perfectStore?.formData;
  const countryCode = useAppSelector(selectCountryCode);
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const [formData, setFormData] = useState(formDataFromStore);
  const [isCancel, setIsCancel] = useState(false);
  const {handleDraft} = usePerfectStore();

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const showDisplayContent = isDisplayStepApplicable(formData?.targetType);

  const getNavigationState = () => ({
    isEdit,
    disableDraft,
    fromWizard: true,
    ...(location.pathname ? {from: location.pathname} : {})
  });

  const getReviewRoute = () =>
    getPerfectStoreWizardReviewRoute({
      isEdit,
      id: perfectStore?.id
    });

  const persistFormData = () =>
    dispatch(
      setPerfectStoreFormData({
        ...formDataFromStore,
        ...formData
      })
    );

  const navigateToStep = (route) => {
    persistFormData();
    navigate(route, {state: getNavigationState()});
  };

  const handleBeforeBack = () => {
    persistFormData();
  };

  const handleStepClick = (step) => {
    const stepRoutes = {
      1: ROUTES.PERFECT_STORE_ADD,
      3: getReviewRoute()
    };
    if (stepRoutes[step]) {
      navigateToStep(stepRoutes[step]);
    }
  };

  const handleNextAction = () => {
    persistFormData();
    navigate(getReviewRoute(), {state: getNavigationState()});
  };

  const handleDraftAction = async () => {
    try {
      persistFormData();
      const response = await handleDraft(formData);
      if (response.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error saving Perfect Store draft:', error);
    }
  };

  const handleCancel = () => setIsCancel(!isCancel);

  const onClickPrimaryButton = () => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE);
  };

  const getFormError = () =>
    showDisplayContent
      ? isPerfectStoreDisplayInvalid(formData, languages)
      : false;

  return (
    <>
      <PerfectStoreWizardLayout
        currentStep={2}
        title={CREATE_PERFECT_STORE_TITLE}
        onStepClick={handleStepClick}
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}
        beforeBack={handleBeforeBack}>
        {showDisplayContent ? (
          <PerfectStoreDisplay
            formData={formData}
            setFormData={setFormData}
            languages={languages}
          />
        ) : (
          <PageContainer data-testid='perfect-store-display-stub'>
            <p>
              Display content for this target type will be available in a later
              release.
            </p>
          </PageContainer>
        )}
      </PerfectStoreWizardLayout>
      {isCancel &&
        getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        })}
    </>
  );
};
